/**
 * MorningBriefing — daily landing tab: KPI tiles + action queues.
 *
 * Queues (each hidden when empty):
 *  1. Payments countdown (planEndDate expired / within 7 days / paying without end date)
 *  2. Seat mismatches (active therapists > paid seats)
 *  3. Support tickets waiting for admin
 *  4. Pending call requests
 *  5. Stale approved prospects (approved 7+ days, never registered)
 *  6. New staff therapists added by clinics, with resolve buttons
 *  7. Churn risks (churned ex-payers / trial expiring)
 *  8. Monitoring alerts — one clinic-health card per clinic (MonitoringAlertsSection);
 *     inactivity / no-appointments live there now, not in the churn queue
 *
 * Owns its queries (RTK dedupes with the parent page's identical queries).
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectTherapistName } from "../../../../store/slices/authSlice";
import {
  Buildings,
  CaretLeft,
  Check,
  CheckCircle,
  Clock,
  CreditCard,
  HandCoins,
  SkipForward,
  XCircle,
  Headset,
  Phone,
  User,
  UserCheck,
  UserPlus,
  Users,
  UsersThree,
  Warning,
  WarningCircle,
  WhatsappLogo,
} from "@phosphor-icons/react";
import PhoneNumber from "../../../../components/ui/PhoneNumber";
import MessagingStatusBadge from "../../../../components/ui/MessagingStatusBadge";
import { useToast } from "../../../../contexts/ToastContext";
import {
  useGetNivClinicsOverviewQuery,
  useGetCallRequestsQuery,
  useGetChurnResolutionsQuery,
  useGetTherapistAdditionsQuery,
  useGetProspectsBoardQuery,
  useGetMonitoringAlertsQuery,
  useResolveChurnAlertMutation,
  useResolveTherapistAdditionMutation,
  useUpdateCallRequestStatusMutation,
  useDismissStaleApprovedProspectMutation,
  useRunWhatsAppActionMutation,
  useGetMessagingUsageQuery,
  useGetNivInboxQuery,
  useSendProspectNudgeMutation,
  useAddNivCrmPaymentMutation,
  useSetStandingOrderMonthStatusMutation,
} from "../../../../store/services/nivApi";
import { useGetAllSupportTicketsQuery } from "../../../../store/services/nivSupportApi";
import {
  CALL_REQUEST_STATUSES,
  CALL_REQUEST_STATUS_LABELS,
  CALL_REQUEST_STATUS_COLORS,
  THERAPIST_ADDITION_STATUSES,
} from "../../../../services/nivFirebase";
import {
  SUPPORT_TICKET_STATUSES,
  STATUS_LABELS as SUPPORT_STATUS_LABELS,
  STATUS_COLORS as SUPPORT_STATUS_COLORS,
  formatTicketDate,
} from "../../../../services/supportFirebase";
import ClinicTags from "../shared/ClinicTags";
import {
  formatDate,
  formatShekels,
  getRelativeDate,
  getTrialDaysLeft,
  buildPaymentsQueue,
  buildStandingOrderQueue,
  buildSeatMismatchQueue,
  buildStaleApprovedQueue,
} from "../shared/nivDerived";
import NivOverviewStats from "../shared/NivOverviewStats";
import "../clinics/NivClinicsTab.css";
import LennyReviewQueue from "../support/LennyReviewQueue";
import MonitoringAlertsSection from "./MonitoringAlertsSection";
import "./MorningBriefing.css";

// Kept local (not imported from NivDashboardPage) to avoid a circular import
const NIV_MESSAGES_PATH = "/dashboard/niv/messages";

const PAYMENT_KIND_META = {
  expired: { cls: "expired", label: "פג תוקף" },
  soon: { cls: "soon", label: null }, // label comes from the queue entry
  missing: { cls: "missing", label: "משלם ללא תאריך סיום" },
};

function MorningBriefing({ isAuthorized, onOpenTicket, onGoToClients }) {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const therapistName = useSelector(selectTherapistName);

  const { data: allRows = [] } = useGetNivClinicsOverviewQuery(undefined, {
    skip: !isAuthorized,
  });
  // Admin-deactivated clinics are excluded from all briefing queues and stats
  const rows = useMemo(() => allRows.filter((r) => !r.inactiveByAdmin), [allRows]);
  const { data: supportTickets = [] } = useGetAllSupportTicketsQuery(undefined, {
    skip: !isAuthorized,
  });
  const { data: callRequests = [] } = useGetCallRequestsQuery(undefined, {
    skip: !isAuthorized,
  });
  const { data: churnResolutions = [] } = useGetChurnResolutionsQuery(undefined, {
    skip: !isAuthorized,
  });
  const { data: therapistAdditions = [] } = useGetTherapistAdditionsQuery(undefined, {
    skip: !isAuthorized,
  });
  const { data: prospectsBoard } = useGetProspectsBoardQuery(undefined, {
    skip: !isAuthorized,
  });
  const { data: monitoringAlerts = [] } = useGetMonitoringAlertsQuery(undefined, {
    skip: !isAuthorized,
  });

  // Messaging router health chips (whatsapp_integration.md §4.3) — 7-day window is enough for 24h counters
  const { data: messagingUsage } = useGetMessagingUsageQuery({ days: 7 }, { skip: !isAuthorized });
  const { data: inboxData } = useGetNivInboxQuery({ unreadOnly: false, limit: 100 }, { skip: !isAuthorized });
  const [resolveChurnAlert] = useResolveChurnAlertMutation();
  const [updateCallRequestStatus] = useUpdateCallRequestStatusMutation();
  const [runWhatsAppAction, { isLoading: waSending }] = useRunWhatsAppActionMutation();
  const [resolveTherapistAddition] = useResolveTherapistAdditionMutation();
  const [dismissStaleApproved] = useDismissStaleApprovedProspectMutation();
  const [sendProspectNudge, { isLoading: prospectNudgeSending }] = useSendProspectNudgeMutation();
  const [addCrmPayment] = useAddNivCrmPaymentMutation();
  const [setStandingOrderMonth] = useSetStandingOrderMonthStatusMutation();
  const [standingOrderBusyKey, setStandingOrderBusyKey] = useState(null);

  // ---------- derived queues ----------

  const paymentsQueue = useMemo(() => buildPaymentsQueue(rows), [rows]);

  // הו"ק: one card per unverified month (from charge day until marked)
  const standingOrderQueue = useMemo(() => buildStandingOrderQueue(rows), [rows]);

  const seatMismatches = useMemo(() => buildSeatMismatchQueue(rows), [rows]);

  const staleApproved = useMemo(
    () => buildStaleApprovedQueue(prospectsBoard?.prospects),
    [prospectsBoard]
  );

  // Clinic health cards: only critical + warning count as morning work
  const messagingChips = useMemo(() => {
    const chips = [];
    const unread = inboxData?.unreadCount || 0;
    if (unread > 0) chips.push({ id: "inbox", tone: "info", label: `${unread} תגובות וואטסאפ לא נקראו`, sub: "inbox" });
    (messagingUsage?.badTemplates || []).forEach((t) =>
      chips.push({ id: `tpl-${t.name}`, tone: "bad", label: `תבנית ${t.name}: ${t.status}`, sub: "templates" })
    );
    const h = messagingUsage?.health;
    if (h?.lastQualityEvent === "FLAGGED" || h?.lastQualityEvent === "DOWNGRADE") {
      chips.push({ id: "quality", tone: "bad", label: `איכות המספר: ${h.lastQualityEvent}`, sub: "usage" });
    }
    const tierLimit = { TIER_250: 250, TIER_1K: 1000, TIER_10K: 10000, TIER_100K: 100000 }[h?.messagingLimit];
    const uniq = messagingUsage?.uniqueRecipientsToday || 0;
    if (tierLimit && uniq >= tierLimit * 0.8) chips.push({ id: "tier", tone: "warn", label: `${uniq}/${tierLimit} נמענים היום (80%+)`, sub: "usage" });
    const l24 = messagingUsage?.last24h;
    if (l24 && (l24.fallbacks > 20 || l24.fallbackRate > 10)) {
      chips.push({ id: "fallback", tone: "warn", label: `${l24.fallbacks} גיבויי SMS ב-24 שעות (${l24.fallbackRate}%)`, sub: "outbox" });
    }
    if (l24?.failed > 0) chips.push({ id: "failed", tone: "bad", label: `${l24.failed} הודעות לא נשלחו ב-24 שעות`, sub: "outbox" });
    return chips;
  }, [messagingUsage, inboxData]);

  const importantAlertsCount = useMemo(
    () =>
      (monitoringAlerts || []).filter(
        (a) => a.severity === "critical" || a.severity === "warning"
      ).length,
    [monitoringAlerts]
  );

  // Overview rows by clinic (tags + owner fallback on the health cards)
  const rowsById = useMemo(
    () => Object.fromEntries(allRows.map((r) => [r.clinicId, r])),
    [allRows]
  );

  const waitingTickets = useMemo(
    () =>
      supportTickets.filter(
        (t) => t.status === SUPPORT_TICKET_STATUSES.WAITING_ADMIN
      ),
    [supportTickets]
  );

  const pendingCalls = useMemo(
    () =>
      (callRequests || []).filter(
        (r) => r.status === CALL_REQUEST_STATUSES.PENDING
      ),
    [callRequests]
  );

  const pendingAdditions = useMemo(
    () =>
      (therapistAdditions || []).filter(
        (a) => a.status === THERAPIST_ADDITION_STATUSES.PENDING
      ),
    [therapistAdditions]
  );

  // Resolved churn alerts, keyed `${clinicId}_${level}` (trial group uses level "trial")
  const resolvedChurnSet = useMemo(
    () => new Set(churnResolutions.map((r) => `${r.clinicId}_${r.level}`)),
    [churnResolutions]
  );

  const churnAlerts = useMemo(() => {
    const notResolved = (clinicId, level) =>
      !resolvedChurnSet.has(`${clinicId}_${level}`);

    // Ex-payers reverted by the cron (plan back to expired trial). Shown in
    // their own group and excluded from the regular trial-expiring list.
    const churned = rows.filter((c) => {
      if (!(c.plan === "trial" && c.previousPaidPlan)) return false;
      return notResolved(c.clinicId, "churned");
    });

    const trialExpiring = rows
      .filter((c) => {
        if (c.plan !== "trial") return false;
        if (c.previousPaidPlan) return false;
        if (!notResolved(c.clinicId, "trial")) return false;
        const daysLeft = getTrialDaysLeft(c.trialEndDate);
        return daysLeft !== null && daysLeft <= 3 && daysLeft >= -7;
      })
      .map((c) => ({ ...c, daysLeft: getTrialDaysLeft(c.trialEndDate) }))
      .sort((a, b) => a.daysLeft - b.daysLeft);

    // Inactivity ("inactive"/"gone") moved to the clinic health cards, which
    // read real therapist login dates instead of clinic.updatedAt.
    return { churned, trialExpiring };
  }, [rows, resolvedChurnSet]);

  const totalChurnAlerts = churnAlerts.churned.length + churnAlerts.trialExpiring.length;

  const allClear =
    paymentsQueue.length === 0 &&
    standingOrderQueue.length === 0 &&
    seatMismatches.length === 0 &&
    staleApproved.length === 0 &&
    waitingTickets.length === 0 &&
    pendingCalls.length === 0 &&
    pendingAdditions.length === 0 &&
    totalChurnAlerts === 0 &&
    importantAlertsCount === 0;

  // ---------- handlers ----------

  const openClinic = (clinicId, tab) =>
    navigate(`/dashboard/niv/clinic/${clinicId}`, tab ? { state: { tab } } : undefined);

  // הו"ק card actions: received → payment log entry for that month; failed →
  // red badge, keeps nagging; skip → month silenced.
  const handleStandingOrderReceived = async (entry) => {
    if (!isAuthorized) return;
    setStandingOrderBusyKey(entry.key);
    try {
      // expectedAmount is pre-VAT (dashboard convention); the record keeps the
      // deal's entered amount as "amount" and the pre-VAT figure separately
      const crm = entry.row.crm || {};
      const preVat = entry.expectedAmount || 0;
      const amount = Number(crm.amount) || preVat;
      await addCrmPayment({
        clinicId: entry.row.clinicId,
        payment: {
          date: `${entry.chargeDate}T09:00:00.000Z`,
          amount,
          month: entry.month,
          method: "standing_order",
          amountBeforeVat: preVat,
          note: null,
        },
      }).unwrap();
      showSuccess(`הו"ק ${entry.label} נרשם כהתקבל`);
    } catch (error) {
      console.error("[CK-9809] Error logging standing-order payment:", {
        errorCode: "CK-9809",
        errorMessage: error?.message,
        clinicId: entry.row.clinicId,
        month: entry.month,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה ברישום התשלום");
    } finally {
      setStandingOrderBusyKey(null);
    }
  };

  const handleStandingOrderStatus = async (entry, action) => {
    if (!isAuthorized) return;
    setStandingOrderBusyKey(entry.key);
    try {
      await setStandingOrderMonth({ clinicId: entry.row.clinicId, month: entry.month, action }).unwrap();
      showSuccess(action === "skip" ? `${entry.label} דולג` : `${entry.label} סומן כלא התקבל`);
    } catch (error) {
      console.error("[CK-9811] Error updating standing-order month:", {
        errorCode: "CK-9811",
        errorMessage: error?.message,
        clinicId: entry.row.clinicId,
        month: entry.month,
        action,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון החודש");
    } finally {
      setStandingOrderBusyKey(null);
    }
  };

  const handleResolveChurn = async (clinicId, level) => {
    if (!isAuthorized) return;
    try {
      await resolveChurnAlert({ clinicId, level }).unwrap();
      showSuccess("ההתראה סומנה כמטופלת");
    } catch (error) {
      console.error("[CK-9857] Error resolving churn alert:", {
        errorCode: "CK-9857",
        errorMessage: error?.message,
        clinicId,
        level,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בסימון ההתראה");
    }
  };

  const handleMarkContacted = async (requestId) => {
    if (!isAuthorized) return;
    try {
      await updateCallRequestStatus({
        requestId,
        status: CALL_REQUEST_STATUSES.CONTACTED,
      }).unwrap();
      showSuccess("הבקשה עודכנה");
    } catch (error) {
      console.error("[CK-9858] Error updating call request status:", {
        errorCode: "CK-9858",
        errorMessage: error?.message,
        requestId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון הבקשה");
    }
  };

  const handleResendLeadWhatsApp = async (requestId) => {
    if (!isAuthorized) return;
    try {
      const res = await runWhatsAppAction({
        action: "resendLead",
        payload: { requestId },
      }).unwrap();
      if (res?.ok) showSuccess("הודעת הוואטסאפ נשלחה");
      else showError(res?.error?.metaMessage || "שליחת הוואטסאפ נכשלה");
    } catch (error) {
      console.error("[CK-10403] Error resending lead WhatsApp:", {
        errorCode: "CK-10403",
        errorMessage: error?.message,
        requestId,
        timestamp: new Date().toISOString(),
      });
      showError("שליחת הוואטסאפ נכשלה");
    }
  };

  // Phase 4.7 — the wa.me deep link became a real send through the router
  const handleProspectNudge = async (prospect) => {
    try {
      const result = await sendProspectNudge({ phone: prospect.id || prospect.phone, fullName: prospect.fullName || "" }).unwrap();
      if (result?.ok) showSuccess(result.channel === "whatsapp" ? "התזכורת נשלחה בוואטסאפ" : "התזכורת נשלחה ב-SMS");
      else if (result?.outcome === "skipped_disabled_by_niv") showError("סוג ההודעה prospect_reg_nudge כבוי — הפעל אותו בלשונית הודעות");
      else showError("השליחה נכשלה");
    } catch (error) {
      console.error("[CK-10518] Prospect nudge send failed:", {
        errorCode: "CK-10518",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("השליחה נכשלה");
    }
  };

  const handleDismissStaleApproved = async (phone) => {
    if (!isAuthorized || !phone) return;
    try {
      await dismissStaleApproved({ phone, author: therapistName }).unwrap();
      showSuccess("ההתראה סומנה כמטופלת");
    } catch (error) {
      console.error("[CK-9867] Error dismissing stale approved prospect:", {
        errorCode: "CK-9867",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בסימון ההתראה");
    }
  };

  const handleResolveAddition = async (additionId) => {
    if (!isAuthorized) return;
    try {
      await resolveTherapistAddition({ additionId }).unwrap();
      showSuccess("ההתראה סומנה כמטופלת");
    } catch (error) {
      console.error("[CK-9885] Error resolving therapist addition:", {
        errorCode: "CK-9885",
        errorMessage: error?.message,
        additionId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בסימון ההתראה");
    }
  };

  // ---------- churn card renderer ----------

  const renderChurnCard = (clinic, level) => (
    <div
      key={clinic.clinicId}
      className={`nivmb-card nivmb-churn-${level}`}
      onClick={() => openClinic(clinic.clinicId)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && openClinic(clinic.clinicId)}
    >
      <div className="nivmb-card-head">
        <strong>{clinic.name || "ללא שם"}</strong>
        {level === "trial" ? (
          <span
            className={`nivmb-days-badge${
              clinic.daysLeft <= 0
                ? " is-expired"
                : clinic.daysLeft <= 1
                ? " is-critical"
                : ""
            }`}
          >
            {clinic.daysLeft <= 0
              ? `פג לפני ${Math.abs(clinic.daysLeft)} ימים`
              : clinic.daysLeft === 1
              ? "יום אחרון!"
              : `${clinic.daysLeft} ימים`}
          </span>
        ) : (
          <ClinicTags row={clinic} compact />
        )}
      </div>
      <div className="nivmb-card-meta">
        <span>
          <User size={11} weight="fill" /> {clinic.ownerName || "—"}
        </span>
        {clinic.ownerPhone && (
          <PhoneNumber
            phone={clinic.ownerPhone}
            className="nivmb-phone-link"
            iconSize={11}
            stopPropagation
          />
        )}
        {level === "trial" ? (
          <span>
            <Users size={11} weight="fill" /> {clinic.totalPatients} מטופלים
          </span>
        ) : (
          <span className="nivmb-last-seen">
            חיבור אחרון: {getRelativeDate(clinic.updatedAt)}
          </span>
        )}
      </div>
      <div className="nivmb-card-actions">
        <button
          type="button"
          className="nivmb-resolve-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleResolveChurn(clinic.clinicId, level);
          }}
        >
          <Check size={14} weight="bold" /> טופל
        </button>
      </div>
    </div>
  );

  // ---------- render ----------

  return (
    <div className="nivmb-briefing">
      {/* 1. Overview hero cards (shared with the clinics tab) */}
      <NivOverviewStats rows={rows} inactiveCount={allRows.length - rows.length} />

      {/* 1b. Messaging router health (whatsapp_integration.md §4.3) */}
      {messagingChips.length > 0 && (
        <section className="nivmb-section">
          <div className="nivmb-section-head nivmb-head-messaging">
            <WhatsappLogo size={20} weight="fill" />
            <h4>הודעות ({messagingChips.length})</h4>
            <button type="button" className="nivmb-head-link" onClick={() => navigate(`${NIV_MESSAGES_PATH}/usage`)}>
              לשימוש
              <CaretLeft size={12} />
            </button>
          </div>
          <div className="nivmb-msg-chips">
            {messagingChips.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`nivmb-msg-chip nivmb-msg-chip-${c.tone}`}
                onClick={() => navigate(`${NIV_MESSAGES_PATH}/${c.sub}`)}
              >
                {c.label}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* 1b. הו"ק verification — one card per unverified month */}
      {standingOrderQueue.length > 0 && (
        <section className="nivmb-section">
          <div className="nivmb-section-head nivmb-head-so">
            <CreditCard size={20} weight="fill" />
            <h4>הו"ק לאישור ({standingOrderQueue.length})</h4>
          </div>
          <div className="nivmb-cards-grid">
            {standingOrderQueue.map((entry) => {
              const busy = standingOrderBusyKey === entry.key;
              return (
                <div key={entry.key} className="nivmb-card nivmb-card-so">
                  <div className="nivmb-card-head">
                    <strong>{entry.row.name || "ללא שם"}</strong>
                    <span className="nivmb-pay-badge nivmb-pay-month">{entry.label}</span>
                  </div>
                  <div className="nivmb-card-meta">
                    <ClinicTags row={entry.row} compact />
                    <span className="nivmb-last-seen">יום חיוב {formatDate(entry.chargeDate)}</span>
                    {entry.expectedAmount != null && (
                      <span className="nivmb-last-seen">צפוי: {formatShekels(entry.expectedAmount)}</span>
                    )}
                    {entry.failedAt && (
                      <span className="nivmb-pay-badge nivmb-pay-expired">
                        סומן כלא התקבל {formatDate(entry.failedAt)}
                      </span>
                    )}
                  </div>
                  <div className="nivmb-card-actions nivmb-so-actions">
                    <button
                      type="button"
                      className="nivmb-resolve-btn"
                      onClick={() => handleStandingOrderReceived(entry)}
                      disabled={busy}
                    >
                      <Check size={14} weight="bold" />
                      התקבל
                    </button>
                    <button
                      type="button"
                      className="nivmb-resolve-btn nivmb-btn-danger"
                      onClick={() => handleStandingOrderStatus(entry, "fail")}
                      disabled={busy}
                    >
                      <XCircle size={14} weight="fill" />
                      לא התקבל
                    </button>
                    <button
                      type="button"
                      className="nivmb-resolve-btn nivmb-btn-muted"
                      onClick={() => handleStandingOrderStatus(entry, "skip")}
                      disabled={busy}
                      title="דלג על החודש הזה — לא יירשם תשלום"
                    >
                      <SkipForward size={14} />
                      דלג
                    </button>
                    <button
                      type="button"
                      className="nivmb-open-btn"
                      onClick={() => openClinic(entry.row.clinicId, "manage")}
                    >
                      פתח
                      <CaretLeft size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 2. Payments queue */}
      {paymentsQueue.length > 0 && (
        <section className="nivmb-section">
          <div className="nivmb-section-head nivmb-head-payments">
            <HandCoins size={20} weight="fill" />
            <h4>תשלומים לטיפול ({paymentsQueue.length})</h4>
          </div>
          <div className="nivmb-cards-grid">
            {paymentsQueue.map(({ row, kind, label, expectedAmount, cycleLabel }) => {
              const meta = PAYMENT_KIND_META[kind] || PAYMENT_KIND_META.missing;
              return (
                <div key={row.clinicId} className="nivmb-card">
                  <div className="nivmb-card-head">
                    <strong>{row.name || "ללא שם"}</strong>
                    <span className={`nivmb-pay-badge nivmb-pay-${meta.cls}`}>
                      {meta.label || label}
                    </span>
                  </div>
                  <div className="nivmb-card-meta">
                    <ClinicTags row={row} compact />
                    {row.planEndDate && (
                      <span className="nivmb-last-seen">
                        פעיל עד {formatDate(row.planEndDate)}
                      </span>
                    )}
                    {expectedAmount != null && (
                      <span className="nivmb-last-seen">
                        צפוי: {formatShekels(expectedAmount)}
                        {cycleLabel ? ` · ${cycleLabel}` : ""}
                      </span>
                    )}
                  </div>
                  <div className="nivmb-card-actions">
                    <button
                      type="button"
                      className="nivmb-open-btn"
                      onClick={() => openClinic(row.clinicId, "manage")}
                    >
                      פתח
                      <CaretLeft size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 2b. Seat mismatches — paying clinics running more therapists than paid */}
      {seatMismatches.length > 0 && (
        <section className="nivmb-section">
          <div className="nivmb-section-head nivmb-head-seats">
            <UsersThree size={20} weight="fill" />
            <h4>חריגות מושבים ({seatMismatches.length})</h4>
          </div>
          <div className="nivmb-cards-grid">
            {seatMismatches.map(({ row, mismatch }) => (
              <div key={row.clinicId} className="nivmb-card">
                <div className="nivmb-card-head">
                  <strong>{row.name || "ללא שם"}</strong>
                  <span className="nivmb-pay-badge nivmb-pay-expired">
                    {mismatch.active} פעילים / {mismatch.paid} בתשלום
                  </span>
                </div>
                <div className="nivmb-card-meta">
                  <ClinicTags row={row} compact />
                  {row.crm?.seatsNote && (
                    <span className="nivmb-last-seen">{row.crm.seatsNote}</span>
                  )}
                </div>
                <div className="nivmb-card-actions">
                  <button
                    type="button"
                    className="nivmb-open-btn"
                    onClick={() => openClinic(row.clinicId, "manage")}
                  >
                    פתח
                    <CaretLeft size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 2c. Lenny (AI support) answers flagged for a human look */}
      {isAuthorized && <LennyReviewQueue compact maxItems={4} />}

      {/* 3. Support tickets waiting for admin */}
      {waitingTickets.length > 0 && (
        <section className="nivmb-section">
          <div className="nivmb-section-head nivmb-head-support">
            <Headset size={20} weight="fill" />
            <h4>ממתינים לתשובתך ({waitingTickets.length})</h4>
          </div>
          <div className="nivmb-cards-grid">
            {waitingTickets.map((ticket) => (
              <div
                key={ticket.id}
                className="nivmb-card nivmb-ticket-card"
                onClick={() => onOpenTicket(ticket)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && onOpenTicket(ticket)}
              >
                <div className="nivmb-card-head">
                  <strong>{ticket.subject}</strong>
                  <span
                    className="nivmb-status-badge"
                    style={{
                      backgroundColor: `${SUPPORT_STATUS_COLORS[ticket.status]}15`,
                      color: SUPPORT_STATUS_COLORS[ticket.status],
                    }}
                  >
                    {SUPPORT_STATUS_LABELS[ticket.status]}
                  </span>
                </div>
                <div className="nivmb-card-meta">
                  <span>
                    <User size={11} weight="fill" /> {ticket.therapistName}
                  </span>
                  <span>
                    <Buildings size={11} weight="fill" /> {ticket.clinicName}
                  </span>
                </div>
                <div className="nivmb-card-footer">
                  <span>{formatTicketDate(ticket.createdAt)}</span>
                  <span>{ticket.messages?.length || 0} הודעות</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 4. Pending call requests */}
      {pendingCalls.length > 0 && (
        <section className="nivmb-section">
          <div className="nivmb-section-head nivmb-head-calls">
            <Phone size={20} weight="fill" />
            <h4>בקשות שיחה ממתינות ({pendingCalls.length})</h4>
            <button type="button" className="nivmb-head-link" onClick={onGoToClients}>
              עבור ללקוחות
              <CaretLeft size={12} />
            </button>
          </div>
          <div className="nivmb-cards-grid">
            {pendingCalls.map((request) => (
              <div key={request.id} className="nivmb-card">
                <div className="nivmb-card-head">
                  <strong>
                    {request.fullName || request.clinicName || "לא ידוע"}
                  </strong>
                  <span
                    className="nivmb-status-badge"
                    style={{
                      backgroundColor: `${CALL_REQUEST_STATUS_COLORS[request.status]}15`,
                      color: CALL_REQUEST_STATUS_COLORS[request.status],
                    }}
                  >
                    {CALL_REQUEST_STATUS_LABELS[request.status]}
                  </span>
                </div>
                <div className="nivmb-card-meta">
                  {request.phone && (
                    <PhoneNumber
                      phone={request.phone}
                      className="nivmb-phone-link"
                      iconSize={11}
                    />
                  )}
                  <span className="nivmb-last-seen">
                    {formatDate(request.createdAt)}
                  </span>
                  {(request.messaging || request.whatsapp) && (
                    <MessagingStatusBadge messaging={request.messaging || request.whatsapp} />
                  )}
                </div>
                <div className="nivmb-card-actions">
                  <button
                    type="button"
                    className="nivmb-resolve-btn"
                    onClick={() => handleMarkContacted(request.id)}
                  >
                    <Phone size={14} /> יצרנו קשר
                  </button>
                  {request.phone && (request.whatsapp?.status === "failed" || request.whatsapp?.status === "skipped") && (
                    <button
                      type="button"
                      className="nivmb-resolve-btn nivmb-wa-resend"
                      onClick={() => handleResendLeadWhatsApp(request.id)}
                      disabled={waSending}
                    >
                      <WhatsappLogo size={14} weight="fill" /> שלח שוב בוואטסאפ
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 4b. Approved 7+ days ago, never registered */}
      {staleApproved.length > 0 && (
        <section className="nivmb-section">
          <div className="nivmb-section-head nivmb-head-stale">
            <UserCheck size={20} weight="fill" />
            <h4>אושרו ולא נרשמו ({staleApproved.length})</h4>
            <button type="button" className="nivmb-head-link" onClick={onGoToClients}>
              עבור ללקוחות
              <CaretLeft size={12} />
            </button>
          </div>
          <div className="nivmb-cards-grid">
            {staleApproved.map(({ prospect, daysWaiting }) => (
              <div key={prospect.id || prospect.phone} className="nivmb-card">
                <div className="nivmb-card-head">
                  <strong>{prospect.fullName || prospect.clinicName || "לא ידוע"}</strong>
                  <span className="nivmb-days-badge">
                    אושר לפני {daysWaiting} ימים
                  </span>
                </div>
                <div className="nivmb-card-meta">
                  {prospect.phone && (
                    <PhoneNumber
                      phone={prospect.phone}
                      className="nivmb-phone-link"
                      iconSize={11}
                    />
                  )}
                  <span className="nivmb-last-seen">לא נרשם למערכת</span>
                </div>
                <div className="nivmb-card-actions">
                  {prospect.phone && (
                    <button
                      type="button"
                      className="nivmb-whatsapp-btn"
                      onClick={() => handleProspectNudge(prospect)}
                      disabled={prospectNudgeSending}
                    >
                      <WhatsappLogo size={14} weight="fill" /> שלח תזכורת
                    </button>
                  )}
                  <button
                    type="button"
                    className="nivmb-resolve-btn"
                    onClick={() => handleDismissStaleApproved(prospect.id || prospect.phone)}
                  >
                    <Check size={14} weight="bold" /> טופל
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 5. New staff therapists added by clinics */}
      {pendingAdditions.length > 0 && (
        <section className="nivmb-section">
          <div className="nivmb-section-head nivmb-head-staff">
            <UserPlus size={20} weight="fill" />
            <h4>מטפלים חדשים בקליניקות ({pendingAdditions.length})</h4>
          </div>
          <div className="nivmb-cards-grid">
            {pendingAdditions.map((addition) => (
              <div
                key={addition.id}
                className="nivmb-card"
                onClick={() =>
                  addition.clinicId && openClinic(addition.clinicId)
                }
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  addition.clinicId &&
                  openClinic(addition.clinicId)
                }
              >
                <div className="nivmb-card-head">
                  <strong>{addition.clinicName || "קליניקה ללא שם"}</strong>
                </div>
                <div className="nivmb-card-meta">
                  <span>
                    <User size={11} weight="fill" />{" "}
                    {addition.therapistName || "לא ידוע"}
                  </span>
                  <span className="nivmb-last-seen">
                    {formatDate(addition.createdAt)}
                  </span>
                </div>
                <div className="nivmb-card-actions">
                  <button
                    type="button"
                    className="nivmb-resolve-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResolveAddition(addition.id);
                    }}
                  >
                    <Check size={14} weight="bold" /> טופל
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 6. Churn risks */}
      {totalChurnAlerts > 0 && (
        <section className="nivmb-section">
          <div className="nivmb-section-head nivmb-head-churn">
            <WarningCircle size={20} weight="fill" />
            <h4>סיכוני נטישה ({totalChurnAlerts})</h4>
          </div>

          {churnAlerts.churned.length > 0 && (
            <div className="nivmb-churn-group">
              <div className="nivmb-churn-group-head">
                <Warning size={11} weight="fill" />
                לקוחות ששילמו ונטשו ({churnAlerts.churned.length})
              </div>
              <div className="nivmb-cards-grid">
                {churnAlerts.churned.map((c) => renderChurnCard(c, "churned"))}
              </div>
            </div>
          )}

          {churnAlerts.trialExpiring.length > 0 && (
            <div className="nivmb-churn-group">
              <div className="nivmb-churn-group-head">
                <Clock size={11} weight="fill" />
                ניסיון עומד לפוג ({churnAlerts.trialExpiring.length})
              </div>
              <div className="nivmb-cards-grid">
                {churnAlerts.trialExpiring.map((c) => renderChurnCard(c, "trial"))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* 8. Clinic health cards (dailyAdminMonitoring cron) */}
      <MonitoringAlertsSection
        cards={monitoringAlerts}
        rowsById={rowsById}
        isAuthorized={isAuthorized}
        onOpenClinic={openClinic}
      />

      {/* 7. All clear */}
      {allClear && (
        <div className="nivmb-all-clear">
          <CheckCircle size={24} weight="fill" />
          <h4>אין פעולות ממתינות</h4>
          <p>הכול תחת שליטה — יום טוב! 🎉</p>
        </div>
      )}
    </div>
  );
}

export default MorningBriefing;
